import { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

// SubscriptionContext — display-only signal about the signed-in USER's tier.
//
// IMPORTANT: per Three-Pillar Roadmap §10.1 premium is per-Team, NOT per-User.
// Do NOT use this to gate paid features — use <SubscriptionGate teamId={…}/>
// which reads team.has_full_access. A Full user joining a baseline Circle
// must NOT see paid features for that Circle.
//
// This context survives only for cosmetic surfaces ("you've paid for one
// team" badges, account-page summaries). Gating logic must be team-scoped.
//
// Tiers (PRD §6.1):
//   - 'baseline'  free; Quick exercise mode, simplified Action Plan, no sharing
//   - 'full'      EUR 10/mo (per Circle); Full exercise mode, all features

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();

  const userTier = user?.subscription_tier || 'baseline';
  const userHasAnyPaidTeam = userTier === 'full';

  const value = useMemo(
    // `isSubscribed` and `tier` are deprecated aliases preserved so existing
    // callsites compile while they migrate to team-scoped gating. New code
    // should use <SubscriptionGate teamId={…}/> or read team.has_full_access
    // directly. See SubscriptionContext header for rationale.
    () => ({ userTier, userHasAnyPaidTeam, tier: userTier, isSubscribed: userHasAnyPaidTeam }),
    [userTier, userHasAnyPaidTeam],
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return ctx;
}
